import React, { useState, useEffect } from 'react';
import { useFetch, useScrollRestore } from '../../hooks/useFetch';
import { showGlobalToast, Spinner, ErrorBanner, Badge, ImportButton, ImportResult, EmptyState } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api, { downloadFile } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import AddRawMaterialModal from './AddRawMaterial';
import EditRawMaterialModal from './EditRawMaterial';

export default function RawMaterials() {
  const { isManager } = useAuth();
  const { saveScroll, restoreScroll, applyScroll } = useScrollRestore('sf_rm_scroll');
  const { data, loading, error, refetch } = useFetch('/api/raw-materials');
  const { data: categories } = useFetch('/api/raw-materials/categories');
  useEffect(() => { applyScroll(); }, [data]); // eslint-disable-line
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImport = async (file) => {
    setImporting(true); setImportResult(null);
    try {
      const form = new FormData(); form.append('file', file);
      const { data: res } = await api.post('/api/raw-materials/import', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res); refetch();
    } catch (err) { setImportResult({ error: errMsg(err) }); }
    finally { setImporting(false); }
  };

  const getStatus = (m) => {
    if (parseFloat(m.quantity_on_hand) <= parseFloat(m.critical_stock_level) && parseFloat(m.critical_stock_level) > 0) return 'critical';
    if (parseFloat(m.quantity_on_hand) <= parseFloat(m.min_stock_level)) return 'low';
    return 'ok';
  };

  const filtered = (data || []).filter(m => {
    const s = getStatus(m);
    if (filterStatus !== 'all' && s !== filterStatus) return false;
    if (filterCategory !== 'all' && (m.category || 'Uncategorized') !== filterCategory) return false;
    if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="page-header">
        <h1>Raw Materials</h1>
        <div className="page-actions">
          <button className="btn btn-sm" onClick={() => downloadFile('/api/raw-materials/import/template')}>
            <i className="ti ti-download" /> Template
          </button>
          <ImportButton onFile={handleImport} />
          <button className="btn btn-sm" onClick={() => downloadFile('/api/raw-materials/export/csv')}>
            <i className="ti ti-download" /> Export CSV
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setAddModal(true)}><i className="ti ti-plus" /> Add material</button>
        </div>
      </div>

      {addModal && <AddRawMaterialModal onClose={() => setAddModal(false)} onSaved={() => { refetch(); showGlobalToast('Material added successfully'); }} />}
      {editModal && <EditRawMaterialModal material={editModal} onClose={() => { setEditModal(null); restoreScroll(); }} onSaved={(msg) => { refetch(); showGlobalToast(msg || 'Material updated successfully'); }} />}
      {importing && <div className="alert alert-info"><i className="ti ti-loader" /> Importing…</div>}
      {importResult && <ImportResult result={importResult} />}

      <div className="filter-bar">
        <div className="search-input">
          <i className="ti ti-search" />
          <input placeholder="Search materials…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="critical">Critical</option>
          <option value="low">Low</option>
          <option value="ok">OK</option>
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="all">All categories</option>
          {(categories || []).map(c => <option key={c} value={c}>{c}</option>)}
          <option value="Uncategorized">Uncategorized</option>
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>{isManager && <th style={{width:60}}></th>}<th>Material</th><th>Category</th><th>On hand</th><th>Status</th></tr>
            </thead>
            <tbody>
              {filtered.map(m => {
                const status = getStatus(m);
                return (
                  <tr key={m.id} onClick={() => { saveScroll(); setEditModal(m); }} style={{ cursor: 'pointer' }}>
                    {isManager && <td></td>}
                    <td style={{ fontWeight: 500 }}>{m.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{m.category || '—'}</td>
                    <td>{fmt.number(m.quantity_on_hand)}</td>

                    <td><Badge status={status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <EmptyState icon="ti-box" message="No raw materials found" />}
        </div>
      </div>
    </div>
  );
}
